// Alien guests who visit the teahouse, order a drink and (hopefully) tip.
import { col, lerp, pick, rand } from '../engine/math.js';
import { Node } from '../engine/renderer.js';
import * as M from './models.js';
import { RECIPES, SPECIES } from './data.js';

let GID = 1;

export class Guest {
  /** mirrorOf: {id, species, x, z} when copying a guest the online host already created */
  constructor(game, seatIndex, recipeId, mirrorOf = null) {
    this.game = game;
    this.id = mirrorOf ? mirrorOf.id : GID++;
    this.seat = seatIndex;
    const seatX = game.world.seats[seatIndex];
    const [sx, sz] = game.tileToWorld(seatX, 0);
    this.seatPos = [sx, sz - 0.05];
    // never two guests of the same species at once: the name tag is how you tell them apart
    const taken = new Set(game.guests.filter((q) => q.state !== 'gone').map((q) => q.species.id));
    const free = SPECIES.filter((sp) => !taken.has(sp.id));
    this.species = mirrorOf ? SPECIES.find((sp) => sp.id === mirrorOf.species) : pick(free.length ? free : SPECIES);
    this.parts = M.buildAlien(this.species.id);
    this.node = this.parts.root;
    this.recipe = RECIPES[recipeId];
    const n = this.recipe.ing.length;
    const base = 44 + n * 12 + (this.recipe.fizz ? 12 : 0) + (this.recipe.boba ? 18 : 0);
    this.maxPatience = base * this.species.patience * (game.level.patienceMul || 1) * (game.perkActive('serenity') ? 1.35 : 1) * game.patienceScale;
    this.patience = this.maxPatience;
    this.state = 'beamin';
    this.beamT = 0;
    const b = game.world.bounds;
    const fromLeft = sx < (b.x0 + b.x1) / 2;
    this.x = fromLeft ? b.x0 - 0.45 : b.x1 + 0.45;
    this.z = sz - 0.9;
    this.entry = [this.x, this.z];
    if (mirrorOf) [this.x, this.z] = [mirrorOf.x, mirrorOf.z];
    this.tx = this.x;
    this.tz = this.z;
    this.ty = 0;
    this.ry = 0;
    this.y = 0;
    this.t = Math.random() * 5;
    this.blink = 2;
    this.happy = 0;
    this.shake = 0;
    this.cup = null;
    this.drinkT = 0;
    this.node.position = [this.x, 0, this.z];
    this.spawnT = 1;
    game.scene.add(this.node);
    if (!mirrorOf) {
      game.sfx('guestArrive');
      game.sfx('teleport');
    }
    this._startBeam();
  }
  // ---- transporter: a column of light, the body sparkles into (or out of) existence
  _startBeam() {
    const c = col(this.species.color);
    this.beamCol = [0.55 + c[0] * 0.45, 0.75 + c[1] * 0.25, 1.0];
    if (!this.beamNode) {
      this.beamNode = new Node(M.beamMesh(0.5, 2.4), { fx: { mode: 2, param: 2.4 }, castShadow: false });
      this.beamNode.rotation = [Math.PI, 0, 0];
      this.game.scene.add(this.beamNode);
    }
    this.beamNode.position = [this.x, 1.2, this.z];
    this.beamNode.visible = true;
    this._setBeam(1);
  }
  _setBeam(amount) {
    const v = [amount, this.beamCol[0], this.beamCol[1], this.beamCol[2]];
    this.node.traverse((n) => {
      if (!n.mesh || n.fx) return;
      n.beam = amount > 0 ? v : null;
      n.castShadow = amount < 0.5;
    });
  }
  /** k: 0..1 through the effect; materialising = true when beaming in. */
  _beamFx(k, dt, materialising) {
    const g = this.game;
    const env = Math.sin(Math.min(1, k) * Math.PI); // column brightness swells then fades
    const bc = this.beamCol;
    this.beamNode.position = [this.x, 1.2, this.z];
    this.beamNode.tint = [bc[0], bc[1], bc[2], 0.55 * env];
    this.beamNode.scale = [1 + Math.sin(g.time * 30) * 0.03, 1, 1 + Math.cos(g.time * 27) * 0.03];
    // body: invisible sparkles -> shimmering silhouette -> solid (or the reverse)
    const s = materialising ? 1 - smooth((k - 0.12) / 0.78) : smooth((k - 0.1) / 0.75);
    this._setBeam(s);
    // twinkling motes inside the column
    const n = Math.round(dt * 160 * (0.3 + env));
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2, r = Math.sqrt(Math.random()) * 0.42;
      g.scene.particles[0].emit({ pos: [this.x + Math.cos(a) * r, rand(0.05, 1.9), this.z + Math.sin(a) * r], vel: [0, rand(-0.4, 0.4), 0], life: rand(0.18, 0.4), size: rand(0.02, 0.045), size1: 0, color: [bc[0], bc[1], bc[2]], color1: [1, 1, 1], alpha: 2.2, type: 1 });
    }
  }
  _endBeam() {
    this._setBeam(0);
    if (this.beamNode) this.beamNode.visible = false;
  }
  get waiting() {
    return this.state === 'wait';
  }
  get frac() {
    return Math.max(0, this.patience / this.maxPatience);
  }
  serve(cup) {
    this.state = 'drink';
    this.drinkT = 3.5;
    this.happy = 1;
    this.holdCup(cup);
  }
  holdCup(cup) {
    this.cup = cup;
    this.parts.body.add(cup.node);
    cup.node.position = [0.22, this.species.id === 'zeeb' ? 0.55 : 0.4, 0.3];
    cup.node.rotation = [0, 0, 0];
  }
  /** Online guest screens: take the host's state for this guest (q is its snapshot row). */
  mirrorState(state, q) {
    if (state !== this.state) {
      if (this.state === 'beamin') this._endBeam();
      if (state === 'beamout') {
        this._startBeam();
        this._setBeam(0);
      }
      this.state = state;
    }
    [this.tx, this.tz, this.ty] = [q[5], q[6], q[7]];
    this.patience = q[8];
    this.maxPatience = q[9];
    this.beamT = q[10];
    this.drinkT = q[12];
    this.happy = q[13];
    this.shake = Math.max(this.shake, q[14]);
    this.ry = q[15];
  }
  /** Online guest screens: move and animate like the host's guest, between its snapshots. */
  mirror(dt, t) {
    this.t += dt;
    this.spawnT = Math.min(1, this.spawnT + dt * 2.5);
    const k = 1 - Math.exp(-14 * dt);
    this.x += (this.tx - this.x) * k;
    this.z += (this.tz - this.z) * k;
    this.y += (this.ty - this.y) * k;
    this.node.rotation[1] = lerp(this.node.rotation[1], this.ry, k);
    let hop = 0;
    if (this.state === 'beamin') {
      this.beamT += dt;
      this._beamFx(this.beamT / 1.7, dt, true);
    } else if (this.state === 'beamout') {
      this.beamT += dt;
      this._beamFx(this.beamT / 1.3, dt, false);
    } else if (this.state === 'arrive') hop = Math.abs(Math.sin(this.t * 9)) * 0.12;
    else if (this.state === 'leave') hop = Math.abs(Math.sin(this.t * 9)) * 0.1;
    else if (this.state === 'drink') {
      this.drinkT -= dt;
      if (this.cup) this.cup.node.rotation[2] = Math.max(0, Math.sin((3.5 - this.drinkT) * 1.2)) * 0.9;
      hop = this.drinkT > 2.8 ? Math.abs(Math.sin(this.t * 14)) * 0.2 : 0;
    }
    this.pose(dt, t, hop);
  }
  refuse() {
    this.shake = 0.6;
  }
  update(dt, t) {
    const g = this.game;
    this.t += dt;
    this.spawnT = Math.min(1, this.spawnT + dt * 2.5);
    const p = this.parts;
    const walkTo = (tx, tz, speed) => {
      const dx = tx - this.x, dz = tz - this.z;
      const d = Math.hypot(dx, dz);
      if (d < 0.05) return true;
      const s = Math.min(d, speed * dt);
      this.x += (dx / d) * s;
      this.z += (dz / d) * s;
      this.node.rotation[1] = lerp(this.node.rotation[1], Math.atan2(dx, dz), 1 - Math.exp(-10 * dt));
      return false;
    };
    let hop = 0;
    switch (this.state) {
      case 'beamin': {
        this.beamT += dt;
        const k = this.beamT / 1.7;
        this._beamFx(k, dt, true);
        if (k >= 1) {
          this._endBeam();
          this.state = 'arrive';
        }
        break;
      }
      case 'beamout': {
        this.beamT += dt;
        const k = this.beamT / 1.3;
        this._beamFx(k, dt, false);
        if (k >= 1) {
          this._endBeam();
          this.state = 'gone';
        }
        break;
      }
      case 'arrive': {
        // walk along the lounge aisle then to the seat
        const aisleZ = this.seatPos[1] - 0.9;
        if (Math.abs(this.x - this.seatPos[0]) > 0.05) walkTo(this.seatPos[0], aisleZ, 2.2);
        else if (walkTo(this.seatPos[0], this.seatPos[1], 2.2)) {
          this.state = 'wait';
          g.onGuestSeated(this);
        }
        hop = Math.abs(Math.sin(this.t * 9)) * 0.12;
        this.y = lerp(this.y, 0, 0.2);
        break;
      }
      case 'wait': {
        if (!g.perkActive('freeze')) this.patience -= dt;
        this.node.rotation[1] = lerp(this.node.rotation[1], 0, 1 - Math.exp(-6 * dt));
        this.y = lerp(this.y, 0.52, 1 - Math.exp(-8 * dt));
        if (this.patience <= 0) {
          this.state = 'angry';
          this.leaveT = 1.2;
          g.onGuestFail(this);
        }
        break;
      }
      case 'drink': {
        this.drinkT -= dt;
        this.y = lerp(this.y, 0.52, 0.2);
        if (this.cup) this.cup.node.rotation[2] = Math.max(0, Math.sin((3.5 - this.drinkT) * 1.2)) * 0.9;
        if (this.drinkT <= 0) {
          g.onGuestFinished(this);
          this.state = 'leave';
        }
        hop = this.drinkT > 2.8 ? Math.abs(Math.sin(this.t * 14)) * 0.2 : 0;
        break;
      }
      case 'angry':
        this.leaveT -= dt;
        this.shake = 0.3;
        if (this.leaveT <= 0) this.state = 'leave';
        break;
      case 'leave': {
        this.y = lerp(this.y, 0, 0.2);
        const aisleZ = this.seatPos[1] - 0.9;
        if (Math.abs(this.z - aisleZ) > 0.05 && Math.abs(this.x - this.entry[0]) > 0.3) walkTo(this.seatPos[0], aisleZ, 2.4);
        else if (walkTo(this.entry[0], this.entry[1], 2.6)) {
          this.state = 'beamout';
          this.beamT = 0;
          this.node.rotation[1] = 0;
          g.sfx('teleport');
          this._startBeam();
          this._setBeam(0);
        }
        hop = Math.abs(Math.sin(this.t * 9)) * 0.1;
        break;
      }
    }
    this.pose(dt, t, hop);
  }
  /** Breathing, blinking, mood and the red flush of impatience. */
  pose(dt, t, hop) {
    const p = this.parts;
    const breathe = 1 + Math.sin(this.t * 2.2) * 0.03;
    const s = this.spawnT * (this.state === 'gone' ? 0 : 1);
    const floaty = this.species.id === 'nuvari' ? 0.15 + Math.sin(this.t * 1.8) * 0.06 : 0;
    this.node.position = [this.x + Math.sin(this.t * 40) * this.shake * 0.05, this.y + hop + floaty, this.z];
    this.shake = Math.max(0, this.shake - dt);
    const happyS = this.happy > 0 ? 1 + Math.sin(this.happy * Math.PI) * 0.15 : 1;
    this.happy = Math.max(0, this.happy - dt * 1.5);
    this.node.scale = [s * happyS, s * breathe * happyS, s * happyS];
    // blink
    this.blink -= dt;
    let eyeS = 1;
    if (this.blink < 0.12) eyeS = 0.1;
    if (this.blink < 0) this.blink = 2 + Math.random() * 3;
    for (const e of p.eyes) e.scale[1] = e.scale[0] * eyeS;
    // mouth mood
    const mood = this.state === 'angry' || (this.state === 'wait' && this.frac < 0.3) ? -1 : this.state === 'drink' || this.state === 'arrive' ? 1 : 0.3;
    const smile = p.asset ? 0 : Math.PI;
    p.mouth.rotation[2] = mood > 0 ? smile : smile + Math.PI;
    p.mouth.position[1] = p.mouth.position[1];
    // tint red when impatient
    const anger = this.state === 'wait' ? Math.max(0, 0.35 - this.frac) * 2.5 : this.state === 'angry' ? 1 : 0;
    p.body.children[0].emissive = [anger * 0.6 * (0.6 + 0.4 * Math.sin(t * 10)), 0, 0];
  }
  dispose() {
    if (this.node.parent) this.node.parent.remove(this.node);
    if (this.beamNode && this.beamNode.parent) this.beamNode.parent.remove(this.beamNode);
  }
}

function smooth(x) {
  x = Math.max(0, Math.min(1, x));
  return x * x * (3 - 2 * x);
}
